/**
 * Live human-readable equivalents for the minutes-denominated behaviour fields (design §2.3.1:
 * "the minutes fields show a live human-readable equivalent after the suffix (`= 24 h`,
 * `= 30 days`)"). `episodeIdleTimeoutMinutes` and `escalationQueueTimeoutMinutes` (9.13) use this;
 * `idleGraceMinutes` does not (its own range never crosses a full day).
 */

export interface MinutesEquivalent {
  value: number;
  /** `'minutes' | 'hours' | 'days'` — the caller resolves the unit label via `t('chatbot:units.<unit>')`. */
  unit: 'minutes' | 'hours' | 'days';
}

/**
 * Picks the coarsest whole unit that exactly divides `minutes` (days, then hours, then minutes) —
 * so 1440 renders as "24 h" (not "1 days") and 43200 renders as "30 days", matching the design's own
 * two worked examples exactly.
 */
export function minutesEquivalent(minutes: number): MinutesEquivalent | null {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  // A single day (1440) reads better as "24 h" than "1 days" — days only wins once it is more
  // than one, e.g. 2880 → "2 days"; 43200 → "30 days" (the design's own worked examples).
  if (minutes % 1440 === 0 && minutes / 1440 > 1) {
    return { value: minutes / 1440, unit: 'days' };
  }
  if (minutes % 60 === 0) {
    return { value: minutes / 60, unit: 'hours' };
  }
  return { value: minutes, unit: 'minutes' };
}

/**
 * The full "N min [= X h]" string a minutes-denominated field's own suffix/hint shows (design
 * §17.1.6 — a platform-default hint reuses "the field's own live-equivalent rule" verbatim rather
 * than inventing a second format). Shared by `BehaviourCard`/`EscalationAiCard`'s field suffixes and
 * `model/provenance.ts`'s `formatSettingValue`.
 */
export function formatMinutesEquivalent(
  minutes: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const equivalent = minutesEquivalent(minutes);
  const base = `${minutes} ${t('units.minutes')}`;
  if (!equivalent || equivalent.unit === 'minutes') return base;
  return `${base} ${t('units.equals', { value: `${equivalent.value} ${t(`units.${equivalent.unit}`)}` })}`;
}
